<template>
  <div class="columns is-mobile is-gapless is-fullheight">
    <transition name="sidebar">
      <aside v-show="$store.state.visibleSidebar" class="column is-narrow aside hero sidebar">
        <sidebar />
      </aside>
    </transition>
    <transition name="sidebar-replace">
      <aside v-show="$store.state.visibleSidebar" />
    </transition>
    <div id="site-wrapper" class="column overflow-hidden">
      <main class="site-content">
        <navbar />
        <router-view />
      </main>
      <dash-footer />
    </div>

    <!-- AI Floating Control (stable, no jump) -->
    <div class="ai-fab" :class="{ 'is-hidden': aiChatOpen }">
      <!-- Bubble (unfolded) -->
      <button
        class="ai-bubble"
        :class="{ 'is-off': aiFabFolded }"
        type="button"
        aria-label="Open AI assistance"
        @click="aiChatOpen = true"
      >
        <span class="ai-bubble-inner">
          <b-icon icon="robot" size="is-medium" />
        </span>
        <span class="ai-bubble-ping" aria-hidden="true" />
      </button>

      <!-- Fold handle (small arrow on bubble) -->
      <button
        class="ai-fold-handle"
        :class="{ 'is-off': aiFabFolded }"
        type="button"
        aria-label="Fold AI assistant"
        @click.stop.prevent="aiFabFolded = true"
      >
        ›
      </button>

      <!-- Folded tab (thin arrow tab, same position, no jump) -->
      <button
        class="ai-fold-tab"
        :class="{ 'is-on': aiFabFolded }"
        type="button"
        aria-label="Unfold AI assistant"
        @click="aiFabFolded = false"
      >
        ‹
      </button>
    </div>

    <!-- AI Chat Modal -->
    <b-modal :active.sync="aiChatOpen" scroll="keep" has-modal-card>
      <div class="modal-card" style="width: 640px; max-height: 80vh;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            AI assistance
          </p>
          <button class="delete" aria-label="close" @click="aiChatOpen = false" />
        </header>
        <section class="modal-card-body" style="display: flex; flex-direction: column; gap: 1rem; max-height: 60vh;">
          <div
            v-if="!aiMessages.length"
            class="has-text-grey"
          >
            Ask the assistant to check the status on a shipping.
          </div>
          <div
            v-else
            class="ai-chat-messages"
            style="flex: 1; overflow-y: auto;"
          >
            <article
              v-for="(m, idx) in aiMessages"
              :key="idx"
              class="message"
              :class="m.from === 'user' ? 'is-info' : 'is-success'"
            >
              <div class="message-body">
                <strong>{{ m.from === 'user' ? 'You' : 'Assistant' }}</strong>
                <br>
                <span style="white-space: pre-wrap;">{{ m.text }}</span>
              </div>
            </article>
          </div>
        </section>
        <footer class="modal-card-foot" style="align-items: flex-end;">
          <b-field style="flex: 1; margin-right: 0.75rem;">
            <b-input
              v-model="aiInput"
              type="textarea"
              placeholder="Type your question and press Enter..."
              :disabled="aiLoading"
              @keyup.enter.native.exact.prevent="sendAiMessage"
            />
          </b-field>
          <b-button
            type="is-primary"
            :loading="aiLoading"
            :disabled="!aiInput || !aiInput.trim() || aiLoading"
            @click="sendAiMessage"
          >
            Send
          </b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Assistance from '@/services/v2/assistance'

export default {
  data () {
    return {
      aiChatOpen: false,
      aiMessages: [],
      aiInput: '',
      aiLoading: false,
      aiFabFolded: false
    }
  },
  watch: {
    aiChatOpen (v) {
      if (v) this.aiFabFolded = true // 👈 auto-fold when modal opens
    }
  },
  methods: {
    async sendAiMessage () {
      if (!this.aiInput || !this.aiInput.trim() || this.aiLoading) return

      const text = this.aiInput.trim()
      this.aiMessages.push({ from: 'user', text })
      this.aiInput = ''
      this.aiLoading = true

      try {
        const history = this.aiMessages.map(m => ({
          role: m.from === 'user' ? 'user' : 'assistant',
          content: m.text
        }))
        const result = await Assistance.chat({ message: text, history })
        const replyText = result.reply || result.message || result.content || 'Sorry, I could not understand the response.'
        this.aiMessages.push({ from: 'assistant', text: replyText })
      } catch (e) {
        this.$buefy.toast.open({
          message: e.message || 'Failed to reach AI assistance.',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.aiLoading = false
      }
    }
  }
}
</script>

<style>
.sidebar-enter,
.sidebar-leave-to {
  transform: translateX(-100%);
  position: absolute;
  opacity: 0;
}

.sidebar-leave,
.sidebar-enter-to {
  transform: translateX(0);
  position: absolute;
  opacity: 1;
}

.sidebar-enter-active,
.sidebar-leave-active { transition: all 300ms }

.sidebar-replace-enter,
.sidebar-replace-leave-to {
  width: 0px;
}

.sidebar-replace-leave,
.sidebar-replace-enter-to {
  width: 280px;
}
.sidebar-replace-enter-active,
.sidebar-replace-leave-active { transition: all 300ms }

.overflow-hidden {
  overflow: hidden;
}

.ai-fab {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9999;

  /* IMPORTANT: stable box so nothing "jumps" */
  width: 54px;
  height: 54px;
}

.ai-fab.is-hidden {
  opacity: 0;
  transform: translateY(10px);
  pointer-events: none;
  transition: opacity 160ms ease, transform 160ms ease;
}

/* ========== Bubble ========== */
.ai-bubble {
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;

  transition: opacity 180ms ease, transform 180ms ease;
}

.ai-bubble.is-off {
  opacity: 0;
  transform: scale(0.92);
  pointer-events: none;
}

.ai-bubble-inner {
  width: 54px;
  height: 54px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: #00d1b2;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.28);
}

.ai-bubble-ping {
  position: absolute;
  inset: -6px;
  border-radius: 999px;
  border: 2px solid rgba(0, 209, 178, 0.35);
  animation: aiPing 1.8s ease-out infinite;
}

@keyframes aiPing {
  0% { transform: scale(0.85); opacity: 0; }
  30% { opacity: 0.7; }
  100% { transform: scale(1.25); opacity: 0; }
}

/* Fold arrow (clickable!) */
.ai-fold-handle {
  position: absolute;
  top: 50%;
  right: -10px;
  transform: translateY(-50%);

  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  cursor: pointer;

  display: grid;
  place-items: center;
  font-size: 18px;
  line-height: 1;
  color: #4a4a4a;

  z-index: 2; /* above bubble */
  transition: opacity 180ms ease, transform 180ms ease;
}

.ai-fold-handle.is-off {
  opacity: 0;
  transform: translateY(-50%) scale(0.92);
  pointer-events: none;
}

/* ========== Folded tab ========== */
.ai-fold-tab {
  position: absolute;
  top: 0;
  right: -20px; /* attach to screen edge */

  width: 22px;
  height: 54px;
  border: none;
  border-radius: 12px 0 0 12px;
  background: rgba(255,255,255,0.96);
  box-shadow: 0 10px 26px rgba(0,0,0,0.25);
  cursor: pointer;

  display: grid;
  place-items: center;
  font-size: 22px;
  color: #4a4a4a;

  /* hidden by default */
  opacity: 0;
  transform: translateX(14px);
  pointer-events: none;

  transition: opacity 180ms ease, transform 180ms ease;
}

.ai-fold-tab.is-on {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

/* mobile */
@media (max-width: 768px) {
  .ai-fab { right: 14px; bottom: 74px; }
  .ai-fold-tab { right: -14px; }
}

</style>
